<script lang="ts">
  import { onMount } from "svelte";
  import type {
    IntegrationDetails,
    ExternalContacts,
    IntegrationChat,
    SupplyLineMapping,
  } from "../../helpers/types";

  import { store } from "../../helpers/store";
  import { track } from "../../helpers/mixpanel";

  import {
    setSetupStep,
    setupStepStore,
    mappableTrackingCategoriesStore,
  } from "../../stores/integrations-store";

  import { saveContactMapping } from "../../helpers/integrations-api";
  import StepSuccessIcon from "./StepSuccessIcon.svelte";
  import { selectedBuyer } from "../../stores/buyer";

  export let integration: IntegrationDetails;
  export let contacts: ExternalContacts | null;
  export let stepNumber: number = 0;
  export let isActive: boolean = false;
  export let authenticated: boolean = false;
  export let currentStep: number = 0;

  $: active = (stepNumber && authenticated && isActive) || (authenticated && !stepNumber);

  let supplyLineMappings: Record<string, SupplyLineMapping> = {};

  const [proChats, nonProChats] = integration.chats?.reduce(
    (acc, curr) => {
      if (integration.non_pro_enabled) {
        acc[0].push(curr);
        return acc;
      }
      if (curr.pro_chat === "true") {
        // for testing
        // if (Math.random() > 0.3) {
        //   if (Math.random() > 0.5) {
        // curr.order_id = 2342342;
        // }
        acc[0].push(curr);
      } else {
        acc[1].push(curr);
      }
      return acc;
    },
    [[], []] as IntegrationChat[][]
  ) || [[], []];

  const proChatsSortedByHasOrder = proChats.sort((a, b) => {
    if (a.order_id && !b.order_id) {
      return -1;
    }

    if (b.order_id && !a.order_id) {
      return 1;
    }

    return 0;
  });

  $: mappingsComplete = true;

  const checkMappings = (chat: IntegrationChat | SupplyLineMapping) => {
    if (!chat.external_supplier_contact) {
      return false;
    }

    if (
      Object.keys(contacts?.account_codes || []).length > 0 &&
      chat.external_account_code === ""
    ) {
      return false;
    }
  };

  onMount(() => {
    proChatsSortedByHasOrder.forEach((chat) => {
      supplyLineMappings[chat.chat_id] = {
        supplier_id: chat.supplier_id,
        external_supplier_contact: chat.external_supplier_contact || "",
        external_account_code: chat.external_account_code || "",
        integration_id: chat.integration_id,
      };

      mappingsComplete = checkMappings(chat) === false ? false : mappingsComplete;
    });
    mappableTrackingCategoriesStore.set(supplyLineMappings);
  });

  const updateSupplylineMappings = (chat: IntegrationChat, mappingType: string, event: any) => {
    const item: string = event.target.value;

    track("integration_external_supplyline_update", {
      buyer_id: $selectedBuyer?.id,
      buyer_name: $selectedBuyer?.name,
      integration_id: integration.id,
      platform: integration.platform,
      external_site_id: integration.site_id,
      mapping_type: mappingType,
      chat_id: chat.chat_id,
      chat_name: chat.chat_name,
      mapped_value: item,
      type: stepNumber ? "setup" : "management",
    });

    if (mappingType === "contact") {
      supplyLineMappings[chat.chat_id].external_supplier_contact = item;
    } else if (mappingType === "account") {
      supplyLineMappings[chat.chat_id].external_account_code = item;
    }

    // Default to true and if any chats are not mapped set the flag to false otherwise leave it alone
    mappingsComplete = true;
    Object.values(supplyLineMappings).forEach((mapping) => {
      mappingsComplete = checkMappings(mapping) !== false ? mappingsComplete : false;
    });

    saveContactMapping(supplyLineMappings[chat.chat_id]).then(() => {
      setSetupStep(integration, mappingsComplete);
    });

    mappableTrackingCategoriesStore.set(supplyLineMappings);
  };

  const contactUsClick = function () {
    track("integration_supplylines_contact_us_click", {
      buyer_id: $selectedBuyer?.id,
      buyer_name: $selectedBuyer?.name,
      integration_id: integration.id,
      platform: integration.platform,
      external_site_id: integration.site_id,
      type: stepNumber ? "setup" : "management",
    });
  };
</script>

<div class="container">
  <div class="supply-line-header">
    <StepSuccessIcon
      success={authenticated && mappingsComplete && !!proChatsSortedByHasOrder.length}
      {isActive}
      {stepNumber}
    />
    <h4 class="subtitle">supply lines</h4>
    {#if authenticated && currentStep > stepNumber}
      <button class="contact-us-btn btn-neutral" on:click={() => setupStepStore.set(stepNumber)}
        >edit
      </button>
    {/if}
  </div>
  {#if active}
    <div class="content">
      {#if integration?.chats?.length}
        {#if proChatsSortedByHasOrder?.length}
          {#each proChatsSortedByHasOrder as chat}
            <div class="supply-line-row">
              {#if supplyLineMappings[chat.chat_id] && checkMappings(supplyLineMappings[chat.chat_id]) !== false}
                <img
                  class="supply_line_icon"
                  alt={`success-tick`}
                  src="static/assets/icons/small_tick.svg"
                />
              {:else}
                <div class="supply_line_dot" />
              {/if}
              <div class="chatname-container">
                <span class="supply-line-chatname">{chat.chat_name}</span>
                {#if !chat.order_id}
                  <span class="no-orders">no orders yet</span>
                {/if}
              </div>
              <div class="select-container">
                <select
                  on:change={(event) => updateSupplylineMappings(chat, "contact", event)}
                  value={supplyLineMappings[chat.chat_id]?.external_supplier_contact}
                >
                  <option value={""} selected>Select Contact</option>
                  {#each Object.entries(contacts?.contacts || []) as contact}
                    <option value={contact[1]}>{contact[0]}</option>
                  {/each}
                </select>
                {#if Object.entries(contacts?.account_codes || []).length}
                  <select
                    on:change={(event) => updateSupplylineMappings(chat, "account", event)}
                    value={supplyLineMappings[chat.chat_id]?.external_account_code}
                  >
                    <option value={""} selected>Select Account Code</option>
                    {#each Object.entries(contacts?.account_codes || []) as accountCode}
                      <option value={accountCode[0]}>{accountCode[1]}</option>
                    {/each}
                  </select>
                {/if}
              </div>
            </div>
          {/each}
        {:else}
          <div class="empty-container">
            <p class="empty-text">Make the supply lines below pro to get started</p>
          </div>
        {/if}
        {#if nonProChats?.length}
          <div class="non-pro-banner">
            <div class="cta-icon-circle">
              <img class="cta-icon" alt={`logo`} src="static/assets/icons/icon-info.svg" />
            </div>
            <p class="cta-copy">
              automate reconciliation for invoices of these suppliers too - we can set you up
            </p>
            <a
              on:click={contactUsClick}
              href={`mailto:support@rekki.com?subject=Automating%20Invoice%20Integration%20for%20Supply%20Line&body=Hi%20REKKI%20Support%2C%0D%0A%0D%0AI%20want%20to%20integrate%20invoices%20for%20my%20supply%20line${
                nonProChats?.length > 1 ? "s" : ""
              }%20and%20streamline%20the%20process%20for%20${
                $selectedBuyer?.name
              }.%20Can%20you%20assist%20me%20in%20setting%20this%20up%3F%0D%0A%0D%0AThanks%2C%0D%0A${
                $store.user?.first_name + " " + $store.user?.last_name
              }`}
            >
              <button class="contact-us-btn">contact us</button>
            </a>
          </div>
          {#each nonProChats as chat}
            <div class="supply-line-row">
              <div class="supply_line_dot supply_line_dot_non_pro" />
              <span class="supply-line-chatname">{chat.chat_name}</span>
              <div class="select-container">
                <select disabled>
                  <option value={""} selected>On {integration?.platform}</option>
                </select>
                {#if Object.keys(contacts?.account_codes || []).length > 0}
                  <select disabled>
                    <option value={""} selected>account code</option>
                  </select>
                {/if}
              </div>
            </div>
          {/each}
        {/if}
      {:else}
        <div class="empty-container">
          <p class="empty-text">create chats in the app to get started</p>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .container {
    border-radius: 8px;
    background-color: white;
    margin-bottom: 18px;
  }

  .supply-line-header {
    display: flex;
    padding: 18px;
    border-bottom: 1px solid;
    border-color: var(--rekki-neutral-01);
    align-items: center;
  }
  .content {
    padding: 18px;
  }

  .supply-line-row {
    display: flex;
    margin-bottom: 18px;
    align-items: center;
  }

  .supply-line-chatname {
    font-size: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .select-container {
    margin-left: auto;
    justify-content: flex-end;
    display: flex;
    flex: 1;
    max-width: 60%;
  }

  .supply_line_icon {
    height: 36px;
    width: 36px;
  }

  .supply_line_dot {
    border-radius: 50%;
    background-color: var(--rekki-warning);
    height: 8px;
    width: 8px;
    border: none;
    margin-right: 16px;
    margin-left: 12px;
    margin-top: 3px;
    min-width: 8px;
  }

  .supply_line_dot_non_pro {
    background-color: var(--rekki-accent);
  }

  .chatname-container {
    display: flex;
    flex-direction: column;
    max-width: 33%;
  }

  .no-orders {
    font-size: 16px;
    color: var(--rekki-neutral-03);
  }
  select {
    border: none;
    background-color: var(--rekki-neutral-01);
    border-radius: 8px;
    font-size: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    height: 60px;
    padding-left: 8px;
    padding-right: 8px;
    margin-left: 8px;
    min-width: 180px;
    flex: 1;
  }

  .subtitle {
    font-size: 18px;
    font-weight: bold;
  }

  .non-pro-banner {
    border-radius: 8px;
    background-color: var(--rekki-accent-light);
    display: flex;
    align-items: center;
    margin: 24px 0;
    padding: 8px 18px;
    font-size: 16px;
    line-height: 22px;
  }

  .contact-us-btn {
    border-radius: 100px;
    padding: 10px 20px;
    background-color: var(--rekki-dark);
    color: var(--rekki-light);
    border: none;
    justify-self: flex-end;
    font-family: "REKKI-OCD";
    white-space: nowrap;
    text-transform: uppercase;
    margin-left: auto;
    font-size: 16px;
  }

  .btn-neutral {
    background-color: var(--rekki-neutral-01);
    color: var(--rekki-dark);
  }

  .cta-icon-circle {
    border-radius: 50%;
    background-color: var(--rekki-light);
    width: 36px;
    height: 36px;
    margin-right: 18px;
  }

  .cta-icon {
    margin: 9px 17px;
    height: 17px;
  }

  .cta-copy {
    flex: 1;
    font-size: 16px;
  }

  .empty-container {
    display: flex;
    padding: 20px 0;
  }

  .empty-text {
    margin: 0 auto;
  }
</style>
